import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { PortfolioGroupModel } from "../types/PortfolioGroupModel";

interface Props {
    portfolioGroups: PortfolioGroupModel[];
    selectedPortfolioGroup: PortfolioGroupModel | null;
    setSelectedPortfolioGroup(portfolioGroupModel: PortfolioGroupModel): Promise<void>;
    labelText: string;
    primaryColour: string;
    secondaryColour: string;
    secondaryHoverColour: string;
    disabled: boolean;
}

export function PortfolioGroupSelector({
    portfolioGroups,
    selectedPortfolioGroup,
    setSelectedPortfolioGroup,
    labelText,
    primaryColour,
    secondaryColour,
    secondaryHoverColour,
    disabled,
}: Props) {
    async function handleSelection(eventArg: SelectChangeEvent<string>) {
        const selectedPortfolioGroupName = eventArg.target.value;
        const selectedPortfolioGroupModel = portfolioGroups.find(x => x.name === selectedPortfolioGroupName);

        if (selectedPortfolioGroupModel === undefined) {
            return;
        }

        await setSelectedPortfolioGroup(selectedPortfolioGroupModel);
    }

    return (
        <FormControl variant="standard" sx={{ width: 220 }}>
            <InputLabel
                id="portfolio-groups-label"
                sx={{
                    color: secondaryColour,
                    "&.Mui-focused": {
                        color: secondaryHoverColour
                    }
                }}
            >
                <b>{labelText}</b> {getSelectControlLabelExtension(portfolioGroups)}
            </InputLabel>
            <Select
                labelId="portfolio-groups-label"
                value={selectedPortfolioGroup
                    ? selectedPortfolioGroup.name.toString()
                    : ' '}
                onChange={handleSelection}
                size="small"
                sx={{
                    color: primaryColour,
                    '&:before': {
                        borderColor: secondaryColour,
                    },
                    '&:after': {
                        borderColor: secondaryHoverColour,
                    },
                    '&:not(.Mui-disabled):hover::before': {
                        borderColor: secondaryColour,
                    },
                    '.MuiSvgIcon-root': {
                        fill: `${secondaryColour} !important`,
                    }
                }}
                disabled={disabled}
            >
                {portfolioGroups?.sort((a, b) => a.name.localeCompare(b.name)).map((portfolioGroup) => (
                    <MenuItem
                        key={portfolioGroup.id}
                        value={portfolioGroup.name}
                    >
                        {portfolioGroup.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function getSelectControlLabelExtension(portfolioGroups: PortfolioGroupModel[]): string | null {
    return isAnyPortfolioGroupAvailable(portfolioGroups)
        ? `(${portfolioGroups.length ?? 0} available)`
        : null;
}

function isAnyPortfolioGroupAvailable(portfolioGroups: PortfolioGroupModel[]): boolean {
    return portfolioGroups
        ? portfolioGroups.length !== 0
        : false;
}
